import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  COL_PRODUCTS,
  COL_PRODUCTION_REQUEST_LINES,
  COL_PRODUCTION_REQUESTS,
  COL_SCHEDULE_SLOTS,
} from '../../../config/collections'
import { COL_CRM_CLIENTS, COL_CRM_EVEREST_CLIENTS } from '../../../config/auth'
import { PERMISSION_KEYS } from '../../../config/permissions'
import { useAuth } from '../../../context/AuthContext'
import { pb, PB_NO_CANCEL } from '../../../pocketbase'
import { cleanupBeforeProductionRequestLineDelete, confirmLineDeleteImpact } from '../../../lib/deleteProductionRequest'
import {
  buildProductionRequestCreateBody,
  buildProductionRequestLineCreateBody,
  linePriorityFromDraft,
  validateProductionRequestLineDrafts,
} from '../../../lib/productionRequestPayload'
import { productionRequestLineUpdatePatch } from '../../../lib/pbRecordDefaults'
import { pocketbaseErrorMessage } from '../../../lib/pocketbaseErrorMessage'
import { unitLabel } from '../../../lib/units'
import { loadProductBomRows } from '../../../lib/productionRequestBom'
import { estimatePlannedProductionDate } from '../../../lib/productionPlanEstimate'
import { loadMachineIdsByProductIds, productHasCompatibleMachines } from '../../../lib/productMachines'
import { formatDateTimeRu, formatQty, isLineOverdue, SCHEDULE_SLOT_STATUS_LABELS } from '../../../lib/scheduleCalculations'
import { usePostToWorkModal } from '../../../hooks/usePostToWorkModal'
import { PostToWorkModal } from '../../../components/PostToWorkModal'
import { ProductionStatusBadge } from '../../../components/ProductionStatusBadge'
import type { CrmClientOption } from '../../../lib/crmClients'
import { enrichProductionRequestClientFromCrm } from '../../../lib/crmClientNameSync'
import { loadCrmClientOptions } from '../../../lib/crmClients'
import { PRIORITY_DEFAULT, PRIORITY_VALUES, priorityOptionLabel } from '../../../lib/priority'
import type {
  ProductRecord,
  ProductionRequestLineRecord,
  ProductionRequestLineStatus,
  ProductionRequestRecord,
  ProductionRequestStatus,
  ProductComponentRecord,
  ScheduleSlotRecord,
} from '../../../types/records'
import { SearchableSelect } from '../../../components/SearchableSelect'
import { FormField } from '../../../components/ui/FormField'
import { PageHeader } from '../../../components/ui/PageHeader'
import {
  alert,
  btn,
  cn,
  formActionsClass,
  inputClass,
  sectionTitleClass,
  tableCellClass,
  tableClass,
  tableHeadClass,
  tableWrapClass,
} from '../../../lib/ui'

type LineDraft = {
  key: string
  pbId?: string
  productId: string
  quantity: string
  plannedShipManager: string
  plannedProduction: string
  plannedProductionAuto: boolean
  plannedProductionHint: string
  actualShip: string
  priority?: string
  status?: ProductionRequestLineStatus
}

function newLine(): LineDraft {
  return {
    key: crypto.randomUUID(),
    productId: '',
    quantity: '1',
    plannedShipManager: '',
    plannedProduction: '',
    plannedProductionAuto: true,
    plannedProductionHint: '',
    actualShip: '',
    priority: String(PRIORITY_DEFAULT),
  }
}

function clientKey(o: Pick<CrmClientOption, 'source' | 'id'>): string {
  return `${o.source}:${o.id}`
}

function clientSourceLabel(source: string): string {
  if (source === COL_CRM_EVEREST_CLIENTS) return 'Everest'
  if (source === COL_CRM_CLIENTS) return 'Клиенты CRM'
  return source
}

/** Дата из поля type=date для PocketBase (как в форме продукции). */
function dateOrUndefined(v: string): string | undefined {
  const t = v.trim()
  return t.length >= 10 ? t.slice(0, 10) : undefined
}

export function ProductionRequestFormPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { can } = useAuth()
  const canWrite = can(PERMISSION_KEYS.PRODUCTION_REQUESTS_WRITE)
  const isNew = id === 'new' || !id

  const [receivedAt, setReceivedAt] = useState('')
  const [clientSelectKey, setClientSelectKey] = useState('')
  const [crmClients, setCrmClients] = useState<CrmClientOption[]>([])
  const [products, setProducts] = useState<ProductRecord[]>([])
  const [lines, setLines] = useState<LineDraft[]>(() => [newLine()])
  const [bomMap, setBomMap] = useState<Record<string, ProductComponentRecord[]>>({})
  const [legacyClientNotice, setLegacyClientNotice] = useState<{ wasName?: string } | null>(null)
  const [requestStatus, setRequestStatus] = useState<ProductionRequestStatus>('draft')
  const [requestPriority, setRequestPriority] = useState('3')
  const [clientUrgent, setClientUrgent] = useState(false)
  const [machineIdsByProduct, setMachineIdsByProduct] = useState<Map<string, string[]>>(() => new Map())
  const [slotsByLineId, setSlotsByLineId] = useState<Map<string, ScheduleSlotRecord[]>>(() => new Map())
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(!isNew)
  const postToWork = usePostToWorkModal()

  const clientOptions = useMemo(
    () =>
      crmClients.map((c) => ({
        value: clientKey(c),
        primary: c.name,
        secondary: clientSourceLabel(c.source),
      })),
    [crmClients],
  )

  const productOptions = useMemo(
    () =>
      products.map((p) => ({
        value: p.id,
        primary: p.name,
        secondary: p.code?.trim() ? p.code.trim() : undefined,
      })),
    [products],
  )

  const loadClients = useCallback(async () => {
    const opts = await loadCrmClientOptions()
    setCrmClients(opts)
  }, [])

  const loadProductsList = useCallback(async () => {
    try {
      const list = await pb.collection(COL_PRODUCTS).getFullList<ProductRecord>({
        sort: 'name',
        ...PB_NO_CANCEL,
      })
      setProducts(list)
    } catch {
      setProducts([])
    }
  }, [])

  const loadOne = useCallback(async () => {
    if (!id || isNew) return
    setLoading(true)
    setErr('')
    try {
      let req = await pb.collection(COL_PRODUCTION_REQUESTS).getOne<ProductionRequestRecord>(id, PB_NO_CANCEL)
      req = await enrichProductionRequestClientFromCrm(req)
      setReceivedAt(req.receivedAt ? req.receivedAt.slice(0, 10) : '')
      setRequestStatus((req.status as ProductionRequestStatus) || 'draft')
      setRequestPriority(String(req.priority ?? 3))
      setClientUrgent(Boolean(req.clientUrgent))
      const cc = (req.clientCollection || '').trim()
      const cid = (req.clientRecordId || '').trim()

      if (cc && cid) {
        setClientSelectKey(`${cc}:${cid}`)
        setLegacyClientNotice(null)
      } else {
        setClientSelectKey('')
        setLegacyClientNotice({ wasName: (req.clientName || '').trim() || undefined })
      }

      const ln = await pb.collection(COL_PRODUCTION_REQUEST_LINES).getFullList<ProductionRequestLineRecord>({
        filter: `request="${id}"`,
        sort: '+sort',
        ...PB_NO_CANCEL,
      })
      if (ln.length === 0) {
        setLines([newLine()])
      } else {
        setLines(
          ln.map((r) => ({
            key: r.id,
            pbId: r.id,
            productId: typeof r.product === 'string' ? r.product : '',
            quantity: String(r.quantity ?? ''),
            plannedShipManager: r.plannedShipManager ? r.plannedShipManager.slice(0, 10) : '',
            plannedProduction: r.plannedProduction ? r.plannedProduction.slice(0, 10) : '',
            plannedProductionAuto: !r.plannedProduction,
            plannedProductionHint: '',
            actualShip: r.actualShip ? r.actualShip.slice(0, 10) : '',
            priority: String(r.priority ?? PRIORITY_DEFAULT),
            status: r.status,
          })),
        )
        const lineIds = ln.map((r) => r.id)
        const productIds = [...new Set(ln.map((r) => (typeof r.product === 'string' ? r.product : '')).filter(Boolean))]
        const [machinesMap, slots] = await Promise.all([
          loadMachineIdsByProductIds(productIds),
          lineIds.length > 0
            ? pb.collection(COL_SCHEDULE_SLOTS).getFullList<ScheduleSlotRecord>({
                filter: lineIds.map((lid) => `requestLine="${lid}"`).join('||'),
                sort: 'plannedStart',
                expand: 'machine',
                ...PB_NO_CANCEL,
              })
            : Promise.resolve([] as ScheduleSlotRecord[]),
        ])
        setMachineIdsByProduct(machinesMap)
        const slotMap = new Map<string, ScheduleSlotRecord[]>()
        for (const slot of slots) {
          const lid = typeof slot.requestLine === 'string' ? slot.requestLine : ''
          if (!lid) continue
          const arr = slotMap.get(lid) ?? []
          arr.push(slot)
          slotMap.set(lid, arr)
        }
        setSlotsByLineId(slotMap)
      }
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка загрузки заявки'))
    } finally {
      setLoading(false)
    }
  }, [id, isNew])

  useEffect(() => {
    void loadClients()
    void loadProductsList()
  }, [loadClients, loadProductsList])

  useEffect(() => {
    void loadOne()
  }, [loadOne])

  useEffect(() => {
    let cancelled = false
    async function run() {
      const next: Record<string, ProductComponentRecord[]> = {}
      for (const l of lines) {
        if (!l.productId) {
          next[l.key] = []
          continue
        }
        try {
          const rows = await loadProductBomRows(l.productId)
          if (!cancelled) next[l.key] = rows
        } catch {
          if (!cancelled) next[l.key] = []
        }
      }
      if (!cancelled) setBomMap(next)
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [lines])

  useEffect(() => {
    let cancelled = false
    async function run() {
      const productIds = [...new Set(lines.map((l) => l.productId).filter(Boolean))]
      if (productIds.length === 0) {
        if (!cancelled) setMachineIdsByProduct(new Map())
        return
      }
      try {
        const map = await loadMachineIdsByProductIds(productIds)
        if (!cancelled) setMachineIdsByProduct(map)
      } catch {
        if (!cancelled) setMachineIdsByProduct(new Map())
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [lines])

  useEffect(() => {
    let cancelled = false

    async function run() {
      const updates = new Map<string, Partial<LineDraft>>()

      await Promise.all(
        lines.map(async (line) => {
          if (!line.plannedProductionAuto || !line.productId) return
          const rawQty = Number(String(line.quantity).replace(',', '.'))
          const qty = Math.round(rawQty * 1e6) / 1e6
          if (!Number.isFinite(qty) || qty <= 0) return

          const product = products.find((p) => p.id === line.productId)
          if (!product) return

          try {
            const estimate = await estimatePlannedProductionDate(product, qty, {
              notBefore: receivedAt || undefined,
              excludeLineId: line.pbId,
            })
            if (cancelled || !estimate) return
            if (estimate.date === line.plannedProduction && estimate.hint === line.plannedProductionHint) return
            updates.set(line.key, {
              plannedProduction: estimate.date,
              plannedProductionHint: estimate.hint,
            })
          } catch {
            if (!cancelled) {
              updates.set(line.key, { plannedProductionHint: '' })
            }
          }
        }),
      )

      if (cancelled || updates.size === 0) return
      setLines((prev) =>
        prev.map((line) => {
          const patch = updates.get(line.key)
          return patch ? { ...line, ...patch } : line
        }),
      )
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [lines, products, receivedAt])

  function addLine() {
    setLines((prev) => [...prev, newLine()])
  }

  function removeLine(key: string) {
    setLines((prev) => (prev.length <= 1 ? prev : prev.filter((l) => l.key !== key)))
  }

  function patchLine(key: string, patch: Partial<LineDraft>) {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)))
  }

  function resolveClientPayload(): { clientCollection: string; clientRecordId: string; clientName: string } | null {
    if (crmClients.length === 0) {
      setErr('Клиенты CRM не загружены. Проверьте подключение к CRM (VITE_SUPABASE_URL).')
      return null
    }
    if (!clientSelectKey.trim()) {
      setErr('Выберите клиента из списка.')
      return null
    }
    const idx = clientSelectKey.indexOf(':')
    if (idx <= 0) {
      setErr('Некорректный выбор клиента.')
      return null
    }
    const source = clientSelectKey.slice(0, idx)
    const rid = clientSelectKey.slice(idx + 1)
    const opt = crmClients.find((c) => c.source === source && c.id === rid)
    if (!opt) {
      setErr('Клиент не найден в списке CRM.')
      return null
    }
    return {
      clientCollection: source,
      clientRecordId: rid,
      clientName: opt.name,
    }
  }

  async function saveLines(requestId: string, drafts: LineDraft[]) {
    const existing = await pb.collection(COL_PRODUCTION_REQUEST_LINES).getFullList<ProductionRequestLineRecord>({
      filter: `request="${requestId}"`,
    })
    const existingById = new Map(existing.map((r) => [r.id, r]))
    const draftIds = new Set(drafts.map((d) => d.pbId).filter(Boolean) as string[])

    const toRemove = existing.filter((r) => !draftIds.has(r.id))
    if (toRemove.length > 0) {
      const confirmed = await confirmLineDeleteImpact(toRemove.map((r) => r.id))
      if (!confirmed) throw new Error('Сохранение отменено: удаление позиций не подтверждено.')
      await cleanupBeforeProductionRequestLineDelete(toRemove.map((r) => r.id))
      await Promise.all(toRemove.map((r) => pb.collection(COL_PRODUCTION_REQUEST_LINES).delete(r.id)))
    }

    let sort = 0
    for (const row of drafts) {
      const raw = Number(String(row.quantity).replace(',', '.'))
      const qty = Math.round(raw * 1e6) / 1e6
      sort += 1
      const lineBody: Record<string, unknown> = {
        request: requestId,
        product: row.productId,
        quantity: qty,
        sort,
      }
      const pm = dateOrUndefined(row.plannedShipManager)
      const pp = dateOrUndefined(row.plannedProduction)
      const as = dateOrUndefined(row.actualShip)
      if (pm) lineBody.plannedShipManager = pm
      if (pp) lineBody.plannedProduction = pp
      if (as) lineBody.actualShip = as
      const linePriority = linePriorityFromDraft(row)
      lineBody.priority = linePriority

      if (row.pbId && existingById.has(row.pbId)) {
        const prev = existingById.get(row.pbId)!
        await pb.collection(COL_PRODUCTION_REQUEST_LINES).update(
          row.pbId,
          productionRequestLineUpdatePatch(prev, lineBody),
        )
      } else {
        await pb.collection(COL_PRODUCTION_REQUEST_LINES).create(
          buildProductionRequestLineCreateBody(lineBody, linePriority),
        )
      }
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setErr('')
    if (!canWrite) return
    const client = resolveClientPayload()
    if (!client) return

    try {
      const rd = dateOrUndefined(receivedAt)
      if (!rd) {
        setErr('Укажите дату поступления заявки.')
        return
      }
      validateProductionRequestLineDrafts(lines)
      const linesToSave = [...lines]
      for (const row of linesToSave) {
        if (row.plannedProduction.trim()) continue
        const rawQty = Number(String(row.quantity).replace(',', '.'))
        const qty = Math.round(rawQty * 1e6) / 1e6
        const product = products.find((p) => p.id === row.productId)
        if (!product || qty <= 0) continue
        const estimate = await estimatePlannedProductionDate(product, qty, {
          notBefore: receivedAt || undefined,
          excludeLineId: row.pbId,
        })
        if (estimate) row.plannedProduction = estimate.date
      }
      const body = {
        receivedAt: rd,
        clientCollection: client.clientCollection,
        clientRecordId: client.clientRecordId,
        clientName: client.clientName,
        priority: Math.min(5, Math.max(1, Number(requestPriority) || 3)),
        clientUrgent,
      }
      if (isNew) {
        const req = await pb.collection(COL_PRODUCTION_REQUESTS).create<ProductionRequestRecord>(
          buildProductionRequestCreateBody(body) as Omit<
            ProductionRequestRecord,
            'id' | 'collectionId' | 'collectionName' | 'created' | 'updated'
          >,
        )
        await saveLines(req.id, linesToSave)
      } else {
        await saveLines(id!, linesToSave)
        await pb.collection(COL_PRODUCTION_REQUESTS).update(id!, body)
      }
      navigate('/production-requests', { replace: true })
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка сохранения'))
    }
  }

  if (loading && !isNew) {
    return (
      <div>
        <PageHeader asSection title="Заявка" />
        <p>Загрузка…</p>
      </div>
    )
  }

  return (
    <div>
      <PageHeader
        asSection
        title={isNew ? 'Новая заявка на производство' : 'Заявка на производство'}
        lead={
          <>
            Клиент и номенклатура выбираются из справочников: клиенты из CRM (<code>clients</code>,{' '}
            <code>everest_clients</code>), продукция из локальной коллекции <code>products</code>. У каждой позиции
            свои даты: дата планируемой отгрузки (по данным менеджера), план производства и факт отгрузки.
            Комплектация в позиции — из спецификации продукции, умноженная на количество.
          </>
        }
      />
      {!isNew && (
        <div className="mb-4">
          <span className="mr-2 text-sm text-su-text">Статус заявки:</span>
          <ProductionStatusBadge kind="request" status={requestStatus} />
        </div>
      )}
      {err && <div className={alert('err')}>{err}</div>}
      <form onSubmit={(e) => void onSubmit(e)} className="grid max-w-6xl gap-4">
        {!canWrite && (
          <div className={cn(alert('warn'), 'col-span-full')}>
            У вас нет права на изменение заявок — только просмотр.
          </div>
        )}
        {legacyClientNotice && (
          <div className={cn(alert('warn'), 'col-span-full')}>
            Ранее заявка была без привязки к CRM.
            {legacyClientNotice.wasName ? <> В заявке было указано: «{legacyClientNotice.wasName}». </> : null}
            Выберите клиента из списка — при сохранении запись будет привязана к CRM.
          </div>
        )}
        <FormField label="Дата поступления заявки" htmlFor="pr-received">
          <input
            id="pr-received"
            type="date"
            className={inputClass}
            value={receivedAt}
            onChange={(e) => setReceivedAt(e.target.value)}
            required
            readOnly={!canWrite}
          />
        </FormField>

        <FormField label="Клиент" htmlFor="pr-client-search">
          <SearchableSelect
            inputId="pr-client-search"
            options={clientOptions}
            value={clientSelectKey}
            onChange={(v) => {
              setClientSelectKey(v)
              setLegacyClientNotice(null)
            }}
            placeholder={crmClients.length === 0 ? 'Нет данных CRM' : 'Найти клиента…'}
            emptyText="Клиентов не найдено"
            disabled={!canWrite || crmClients.length === 0}
            clearable={false}
            ariaLabel="Клиент"
          />
          {crmClients.length === 0 && (
            <p className="mt-1.5 text-[0.85rem] text-slate-500">
              Подключите CRM (<code>VITE_SUPABASE_URL</code>), чтобы загрузить список клиентов.
            </p>
          )}
        </FormField>

        <FormField label="Приоритет заявки" htmlFor="pr-priority">
          <select
            id="pr-priority"
            className={inputClass}
            value={requestPriority}
            onChange={(e) => setRequestPriority(e.target.value)}
            disabled={!canWrite}
          >
            {PRIORITY_VALUES.map((p) => (
              <option key={p} value={String(p)}>
                {priorityOptionLabel(p)}
                {p === PRIORITY_DEFAULT ? ' · по умолчанию' : ''}
              </option>
            ))}
          </select>
          <p className="m-0 text-[0.8rem] leading-snug text-slate-500">
            1 — в конец очереди, 5 — в начало. Используется при сортировке в планировании.
          </p>
        </FormField>

        <FormField label="Срочность" htmlFor="pr-urgent">
          <label className="flex items-center gap-2 text-sm text-su-text">
            <input
              id="pr-urgent"
              type="checkbox"
              checked={clientUrgent}
              onChange={(e) => setClientUrgent(e.target.checked)}
              disabled={!canWrite}
            />
            Срочный заказ клиента
          </label>
        </FormField>

        <section className="col-span-full">
          <div className="mb-3">
            <h3 className={cn(sectionTitleClass, 'mb-0')}>Позиции</h3>
          </div>
          <div className="flex flex-col gap-4">
            {lines.map((l, idx) => {
              const qtyLine = Number(String(l.quantity).replace(',', '.')) || 0
              const bom = bomMap[l.key] ?? []
              const product = products.find((p) => p.id === l.productId)
              const hasMachines = l.productId ? productHasCompatibleMachines(l.productId, machineIdsByProduct) : true
              const lineSlots = l.pbId ? (slotsByLineId.get(l.pbId) ?? []) : []
              const overdue = l.status
                ? isLineOverdue({ plannedProduction: l.plannedProduction, status: l.status })
                : false
              return (
                <article
                  className="overflow-hidden rounded-[14px] border border-su-border bg-su-surface shadow-su-sm"
                  key={l.key}
                >
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-su-border bg-gradient-to-b from-slate-50 to-slate-100 px-4 py-2.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="inline-flex h-8 min-w-8 items-center justify-center rounded-full bg-indigo-500/12 px-2 text-[0.82rem] font-bold text-indigo-700">
                        {idx + 1}
                      </span>
                      {l.status && <ProductionStatusBadge kind="line" status={l.status} />}
                      {overdue && (
                        <span className="inline-flex rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800">
                          Просрочено
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {!isNew && l.pbId && canWrite && l.productId && (
                        <button
                          type="button"
                          className={btn('default', 'sm')}
                          onClick={() =>
                            postToWork.open({
                              lineId: l.pbId!,
                              productId: l.productId,
                              productName: product?.name ?? '—',
                              quantity: qtyLine,
                              lineStatus: l.status,
                            })
                          }
                        >
                          Поставить в работу
                        </button>
                      )}
                      {canWrite && (
                        <button
                          type="button"
                          className={btn('ghost', 'sm')}
                          onClick={() => removeLine(l.key)}
                          disabled={lines.length <= 1}
                        >
                          Убрать
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-4 p-4">
                    <FormField label="Номенклатура" htmlFor={`pr-product-${l.key}`} className="min-w-0">
                      <SearchableSelect
                        inputId={`pr-product-${l.key}`}
                        options={productOptions}
                        value={l.productId}
                        onChange={(v) =>
                          patchLine(l.key, {
                            productId: v,
                            plannedProductionAuto: true,
                            plannedProduction: '',
                            plannedProductionHint: '',
                          })
                        }
                        placeholder={products.length === 0 ? 'Нет продукции' : 'Найти продукцию…'}
                        emptyText="Продукции не найдено"
                        disabled={!canWrite || products.length === 0}
                        clearable={false}
                        ariaLabel={`Номенклатура, позиция ${idx + 1}`}
                      />
                    </FormField>
                    {l.productId && !hasMachines && (
                      <div className={cn(alert('warn'), 'text-[0.85rem]')}>
                        У выбранной продукции не указаны совместимые станки — постановка в график будет недоступна.
                        Настройте совместимость в карточке продукции.
                      </div>
                    )}
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                      <FormField label="Количество" htmlFor={`pr-qty-${l.key}`} className="min-w-0">
                        <input
                          id={`pr-qty-${l.key}`}
                          type="text"
                          inputMode="decimal"
                          className={cn(inputClass, 'w-full')}
                          value={l.quantity}
                          onChange={(e) =>
                            patchLine(l.key, {
                              quantity: e.target.value,
                              plannedProductionAuto: l.plannedProductionAuto,
                              plannedProductionHint: l.plannedProductionAuto ? '' : l.plannedProductionHint,
                            })
                          }
                          required
                          readOnly={!canWrite}
                        />
                      </FormField>
                      <FormField label="Дата планируемой отгрузки" htmlFor={`pr-ship-m-${l.key}`} className="min-w-0">
                        <input
                          id={`pr-ship-m-${l.key}`}
                          type="date"
                          className={cn(inputClass, 'w-full min-w-0')}
                          value={l.plannedShipManager}
                          onChange={(e) => patchLine(l.key, { plannedShipManager: e.target.value })}
                          readOnly={!canWrite}
                        />
                      </FormField>
                      <FormField label="Производство (план)" htmlFor={`pr-prod-${l.key}`} className="min-w-0">
                        <input
                          id={`pr-prod-${l.key}`}
                          type="date"
                          className={cn(inputClass, 'w-full min-w-0')}
                          value={l.plannedProduction}
                          onChange={(e) =>
                            patchLine(l.key, {
                              plannedProduction: e.target.value,
                              plannedProductionAuto: false,
                              plannedProductionHint: '',
                            })
                          }
                          readOnly={!canWrite}
                        />
                        {l.plannedProductionHint ? (
                          <p className="m-0 mt-1 text-[0.78rem] leading-snug text-slate-500">{l.plannedProductionHint}</p>
                        ) : l.productId && l.plannedProductionAuto ? (
                          <p className="m-0 mt-1 text-[0.78rem] leading-snug text-slate-400">Расчёт по загрузке станков…</p>
                        ) : null}
                      </FormField>
                      <FormField label="Отгрузка факт" htmlFor={`pr-ship-a-${l.key}`} className="min-w-0">
                        <input
                          id={`pr-ship-a-${l.key}`}
                          type="date"
                          className={cn(inputClass, 'w-full min-w-0')}
                          value={l.actualShip}
                          onChange={(e) => patchLine(l.key, { actualShip: e.target.value })}
                          readOnly={!canWrite}
                        />
                      </FormField>
                      <FormField label="Приоритет строки" htmlFor={`pr-priority-${l.key}`} className="min-w-0">
                        <select
                          id={`pr-priority-${l.key}`}
                          className={cn(inputClass, 'w-full min-w-0')}
                          value={l.priority ?? String(PRIORITY_DEFAULT)}
                          onChange={(e) => patchLine(l.key, { priority: e.target.value })}
                          disabled={!canWrite}
                        >
                          {PRIORITY_VALUES.map((p) => (
                            <option key={p} value={String(p)}>
                              {priorityOptionLabel(p)}
                            </option>
                          ))}
                        </select>
                      </FormField>
                    </div>
                  </div>
                  <div className="border-t border-dashed border-su-border bg-slate-50 px-4 py-3.5 pb-4">
                    {!l.productId ? (
                      <p className="m-0 text-[0.85rem] text-slate-400">
                        Выберите номенклатуру — здесь появится комплектация.
                      </p>
                    ) : bom.length === 0 ? (
                      <p className="m-0 text-[0.85rem] text-slate-500">
                        Комплектация для этой продукции не задана.
                      </p>
                    ) : (
                      <>
                        <p className="mb-2 mt-0 text-[0.8rem] font-semibold text-slate-600">
                          Комплектация (на единицу × {qtyLine || '—'})
                        </p>
                        <div className={tableWrapClass}>
                          <table className={cn(tableClass, 'm-0 text-[0.85rem]')}>
                            <thead>
                              <tr>
                                <th className={tableHeadClass}>Расходник</th>
                                <th className={tableHeadClass}>На ед.</th>
                                <th className={tableHeadClass}>К заказу</th>
                                <th className={tableHeadClass}>Ед.</th>
                              </tr>
                            </thead>
                            <tbody>
                              {bom.map((c) => {
                                const cons = c.expand?.consumable
                                const per = Number(c.quantity) || 0
                                const need = per * (qtyLine || 0)
                                return (
                                  <tr key={c.id}>
                                    <td className={cn(tableCellClass, 'px-2 py-1.5')}>{cons?.name ?? '—'}</td>
                                    <td className={cn(tableCellClass, 'px-2 py-1.5')}>{per}</td>
                                    <td className={cn(tableCellClass, 'px-2 py-1.5')}>
                                      {qtyLine ? Math.round(need * 1e6) / 1e6 : '—'}
                                    </td>
                                    <td className={cn(tableCellClass, 'px-2 py-1.5')}>
                                      {cons?.unit ? unitLabel(cons.unit) : '—'}
                                    </td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </div>
                  {lineSlots.length > 0 && (
                    <div className="border-t border-dashed border-su-border bg-white px-4 py-3.5 pb-4">
                      <p className="mb-2 mt-0 text-[0.8rem] font-semibold text-slate-600">Слоты графика</p>
                      <div className={tableWrapClass}>
                        <table className={cn(tableClass, 'm-0 text-[0.85rem]')}>
                          <thead>
                            <tr>
                              <th className={tableHeadClass}>Станок</th>
                              <th className={tableHeadClass}>Начало</th>
                              <th className={tableHeadClass}>Окончание</th>
                              <th className={tableHeadClass}>План</th>
                              <th className={tableHeadClass}>Статус</th>
                            </tr>
                          </thead>
                          <tbody>
                            {lineSlots.map((slot) => {
                              const machine = slot.expand?.machine
                              const machineLabel = machine ? `№${machine.number}` : '—'
                              return (
                                <tr key={slot.id}>
                                  <td className={cn(tableCellClass, 'px-2 py-1.5')}>{machineLabel}</td>
                                  <td className={cn(tableCellClass, 'px-2 py-1.5')}>
                                    {formatDateTimeRu(slot.plannedStart)}
                                  </td>
                                  <td className={cn(tableCellClass, 'px-2 py-1.5')}>
                                    {formatDateTimeRu(slot.plannedEnd)}
                                  </td>
                                  <td className={cn(tableCellClass, 'px-2 py-1.5')}>
                                    {formatQty(slot.plannedQuantity)}
                                  </td>
                                  <td className={cn(tableCellClass, 'px-2 py-1.5')}>
                                    {SCHEDULE_SLOT_STATUS_LABELS[slot.status] ?? slot.status}
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </article>
              )
            })}
          </div>
          {canWrite && (
            <button type="button" className={cn(btn(), 'mt-1')} onClick={addLine}>
              Добавить позицию
            </button>
          )}
        </section>

        <div className={formActionsClass}>
          {canWrite && (
            <button type="submit" className={btn('primary')}>
              Сохранить
            </button>
          )}
          <Link to="/production-requests" className={btn('ghost')}>
            К списку
          </Link>
        </div>
      </form>
      {postToWork.isOpen && postToWork.target && (
        <PostToWorkModal
          target={postToWork.target}
          machineIdsByProduct={machineIdsByProduct}
          onClose={postToWork.close}
        />
      )}
    </div>
  )
}
